import React, { useRef, useEffect, useState, useCallback } from "react";
import {
    Box,
    IconButton,
    Tooltip,
    Divider,
    Typography,
    styled,
} from "@mui/material";
import {
    Title as TitleIcon,
    FormatBold as BoldIcon,
    FormatItalic as ItalicIcon,
    FormatListBulleted as BulletListIcon,
    FormatListNumbered as NumberedListIcon,
    Link as LinkIcon,
    FormatQuote as QuoteIcon,
    Code as CodeIcon,
    FormatLineSpacing as NewLineIcon,
    Undo as UndoIcon,
    Redo as RedoIcon,
} from "@mui/icons-material";

const CardPanel = styled(Box)({
    backgroundColor: "#0d2147",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "10px",
    color: "#ffffff",
    padding: "16px",
    width: "100%",
    boxSizing: "border-box",
});

const ToolbarButton = styled(IconButton)({
    color: "#b0c4de",
    padding: "6px",
    borderRadius: "4px",
    "&:hover": { backgroundColor: "rgba(63, 105, 248, 0.2)", color: "#ffffff" },
    "&.Mui-disabled": { color: "rgba(255,255,255,0.2)" },
});

const EditorTextArea = styled("textarea")({
    width: "100%",
    boxSizing: "border-box",
    minHeight: "420px",
    backgroundColor: "#091730",
    color: "#e0e6f5",
    fontFamily: "'Fira Code', 'Roboto Mono', monospace",
    fontSize: "0.875rem",
    lineHeight: 1.6,
    padding: "16px",
    border: "1px solid rgba(255, 255, 255, 0.15)",
    borderRadius: "6px",
    outline: "none",
    resize: "vertical",
    "&:focus": { borderColor: "#3f69f8", boxShadow: "0 0 0 1px #3f69f8" },
});

const TOOLBAR_ACTIONS = [
    { icon: NewLineIcon, label: "New Line", syntax: "\n", placeholder: "" },
    {
        icon: TitleIcon,
        label: "Heading",
        syntax: "## ",
        placeholder: "Heading",
    },
    { icon: BoldIcon, label: "Bold", syntax: "**", placeholder: "bold text" },
    {
        icon: ItalicIcon,
        label: "Italic",
        syntax: "*",
        placeholder: "italic text",
    },
    { divider: true },
    {
        icon: BulletListIcon,
        label: "Bullet List",
        syntax: "- ",
        placeholder: "list item",
    },
    {
        icon: NumberedListIcon,
        label: "Numbered List",
        syntax: "1. ",
        placeholder: "list item",
    },
    { divider: true },
    { icon: LinkIcon, label: "Link", syntax: "[]", placeholder: "link text" },
    { icon: QuoteIcon, label: "Quote", syntax: "> ", placeholder: "quote" },
    { icon: CodeIcon, label: "Inline Code", syntax: "`", placeholder: "code" },
];

const TEXTAREA_ID = "marketing-markdown-editor";
const HISTORY_LIMIT = 80;

/**
 * Insert markdown at selection. Returns new text and cursor range to select.
 */
function applySyntax(text, start, end, syntax, placeholder) {
    const selected = text.substring(start, end);
    const before = text.substring(0, start);
    const after = text.substring(end);
    const needsLeadingNewline =
        before.length > 0 && !before.endsWith("\n") && syntax !== "\n";

    let insert;
    let selectFrom;
    let selectTo;

    switch (syntax) {
        case "\n":
            insert = "\n";
            selectFrom = start + 1;
            selectTo = start + 1;
            break;
        case "## ":
        case "- ":
        case "1. ": {
            const content = selected || placeholder;
            insert = `${needsLeadingNewline ? "\n" : ""}${syntax}${content}`;
            const contentStart = start + insert.length - content.length;
            selectFrom = contentStart;
            selectTo = contentStart + content.length;
            break;
        }
        case "> ": {
            const lineStart = before.lastIndexOf("\n") + 1;
            let blockEnd = end;
            if (start === end) {
                const nl = after.indexOf("\n");
                blockEnd = start + (nl === -1 ? after.length : nl);
            }
            const block = text.substring(lineStart, blockEnd);
            const content = block || placeholder;
            const lines = content.split("\n");
            const quoted = lines
                .map((line) =>
                    /^>\s?/.test(line) ? line : `> ${line}`,
                )
                .join("\n");
            const newBefore = text.substring(0, lineStart);
            const newAfter = text.substring(blockEnd);
            const newText = newBefore + quoted + newAfter;
            const inner =
                lines
                    .map((line) => line.replace(/^>\s?/, ""))
                    .join("\n") || placeholder;
            const prefixLen = quoted.length - inner.length;
            selectFrom = lineStart + prefixLen;
            selectTo = lineStart + quoted.length;
            return { newText, selectFrom, selectTo };
        }
        case "`": {
            const content = selected || placeholder;
            insert = `\`${content}\``;
            const contentStart = start + 1;
            selectFrom = contentStart;
            selectTo = contentStart + content.length;
            break;
        }
        case "[]": {
            const content = selected || placeholder;
            insert = `[${content}](https://example.com)`;
            const contentStart = start + 1;
            selectFrom = contentStart;
            selectTo = contentStart + content.length;
            break;
        }
        default: {
            const content = selected || placeholder;
            insert = `${syntax}${content}${syntax}`;
            const contentStart = start + syntax.length;
            selectFrom = contentStart;
            selectTo = contentStart + content.length;
            break;
        }
    }

    return { newText: before + insert + after, selectFrom, selectTo };
}

export default function MarkdownEditor({ value, onChange, maxLength = 2000 }) {
    const textAreaRef = useRef(null);
    const pastRef = useRef([value]);
    const futureRef = useRef([]);
    const skipHistorySyncRef = useRef(false);
    const lastExternalValueRef = useRef(value);
    const [historyVersion, setHistoryVersion] = useState(0);
    const bumpHistory = useCallback(() => setHistoryVersion((v) => v + 1), []);

    // Reset history whenever `value` changes from OUTSIDE this component
    // (e.g. parent resets the field). Changes that originate from inside
    // (typing, toolbar clicks, undo/redo) set skipHistorySyncRef so this
    // effect ignores its own echo.
    useEffect(() => {
        if (skipHistorySyncRef.current) {
            skipHistorySyncRef.current = false;
            lastExternalValueRef.current = value;
            return;
        }
        if (value === lastExternalValueRef.current) return;
        lastExternalValueRef.current = value;
        if (value === pastRef.current[pastRef.current.length - 1]) return;
        pastRef.current = [value];
        futureRef.current = [];
        bumpHistory();
    }, [value, bumpHistory]);

    // Push `next` onto the history stack (ensuring the current `value` is
    // captured first if it isn't already the top), and clear redo history.
    const pushHistory = (next) => {
        const past = pastRef.current;
        const withCurrent =
            past[past.length - 1] === value ? past : [...past, value];
        pastRef.current = [...withCurrent, next].slice(-HISTORY_LIMIT);
        futureRef.current = [];
    };

    // Send a value to the parent without re-triggering the sync effect above.
    const propagate = (next) => {
        skipHistorySyncRef.current = true;
        lastExternalValueRef.current = next;
        onChange(next);
        bumpHistory();
    };

    const canUndo = pastRef.current.length > 1;
    const canRedo = futureRef.current.length > 0;

    const focusEditor = () =>
        requestAnimationFrame(() => textAreaRef.current?.focus());

    const commitValue = (next) => {
        if (next === value) return;
        pushHistory(next);
        propagate(next);
    };

    const handleUndo = () => {
        if (!canUndo) return;
        const current = pastRef.current[pastRef.current.length - 1];
        futureRef.current = [current, ...futureRef.current];
        pastRef.current = pastRef.current.slice(0, -1);
        propagate(pastRef.current[pastRef.current.length - 1]);
        focusEditor();
    };

    const handleRedo = () => {
        if (!canRedo) return;
        const [next, ...rest] = futureRef.current;
        futureRef.current = rest;
        pastRef.current = [...pastRef.current, next].slice(-HISTORY_LIMIT);
        propagate(next);
        focusEditor();
    };

    const handleToolbarClick = (syntax, placeholder) => {
        const textarea = textAreaRef.current;
        if (!textarea) return;

        const { selectionStart, selectionEnd } = textarea;
        const { newText, selectFrom, selectTo } = applySyntax(
            value,
            selectionStart,
            selectionEnd,
            syntax,
            placeholder,
        );

        if (newText.length > maxLength) return;

        pushHistory(newText);
        propagate(newText);

        requestAnimationFrame(() => {
            textarea.focus();
            textarea.setSelectionRange(selectFrom, selectTo);
        });
    };

    const handleKeyDown = (e) => {
        if (!(e.metaKey || e.ctrlKey)) return;
        if (e.key === "z" && !e.shiftKey) {
            e.preventDefault();
            handleUndo();
        } else if ((e.key === "z" && e.shiftKey) || e.key === "y") {
            e.preventDefault();
            handleRedo();
        }
    };

    return (
        <CardPanel>
            <Box
                sx={{
                    display: "flex",
                    gap: 0.5,
                    pb: 1.5,
                    mb: 1.5,
                    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                    flexWrap: "wrap",
                    alignItems: "center",
                }}
            >
                <Tooltip title="Undo">
                    <span>
                        <ToolbarButton
                            onClick={handleUndo}
                            disabled={!canUndo}
                            aria-label="Undo"
                        >
                            <UndoIcon fontSize="small" />
                        </ToolbarButton>
                    </span>
                </Tooltip>
                <Tooltip title="Redo">
                    <span>
                        <ToolbarButton
                            onClick={handleRedo}
                            disabled={!canRedo}
                            aria-label="Redo"
                        >
                            <RedoIcon fontSize="small" />
                        </ToolbarButton>
                    </span>
                </Tooltip>
                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        mx: 0.5,
                    }}
                />
                {TOOLBAR_ACTIONS.map((action, i) =>
                    action.divider ? (
                        <Divider
                            key={`div-${i}`}
                            orientation="vertical"
                            flexItem
                            sx={{
                                backgroundColor: "rgba(255, 255, 255, 0.1)",
                                mx: 0.5,
                            }}
                        />
                    ) : (
                        <Tooltip key={action.label} title={action.label}>
                            <ToolbarButton
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() =>
                                    handleToolbarClick(
                                        action.syntax,
                                        action.placeholder,
                                    )
                                }
                            >
                                <action.icon fontSize="small" />
                            </ToolbarButton>
                        </Tooltip>
                    ),
                )}
            </Box>

            <EditorTextArea
                ref={textAreaRef}
                id={TEXTAREA_ID}
                value={value}
                onChange={(e) => commitValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Write your marketing message in Markdown..."
                maxLength={maxLength}
            />

            <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
            >
                <Typography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.4)" }}
                >
                    Markdown supported · Ctrl+Z undo
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        color:
                            value.length > maxLength * 0.9
                                ? "#ffc107"
                                : "rgba(255,255,255,0.4)",
                    }}
                >
                    {value.length}/{maxLength} characters
                </Typography>
            </Box>
        </CardPanel>
    );
}
